import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import ItemDrawer from "./ItemDrawer";
import ReadyPack from "./ReadyPack";
import TrackingBox from "./TrackingBox";
import { Modal, StatusPill } from "./WorkUi";
import { errorMessage, fmtShortDate, fmtTime, parseISODate, statusTone, toISODate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const monthFmt = new Intl.DateTimeFormat("en-IN", { month: "long", year: "numeric" });
const dayFmt = new Intl.DateTimeFormat("en-IN", { weekday: "long", day: "numeric", month: "short" });

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));

/** Post statuses (DM posts) mapped onto the same colour tones as work items. */
const POST_TONE = { idea: "grey", draft: "blue", review: "violet", approved: "teal", scheduled: "indigo", posted: "green", skipped: "grey" };
const eventTone = (e) => (e.Kind === "item" ? statusTone(e.Status) : POST_TONE[String(e.Status || "").toLowerCase()] || "grey");

/** Tracking state of a posted calendar post, with a field to paste / fix its live link. */
function PostTracking({ api, post }) {
  const [tracking, setTracking] = useState(null);
  useEffect(() => {
    api.postTracking(post.Post_Id).then(setTracking).catch(() => setTracking({ status: "none" }));
  }, [api, post.Post_Id]);
  if (!tracking) return null;
  return (
    <TrackingBox
      key={tracking.status}
      tracking={tracking}
      postedUrl={post.Posted_URL}
      onTrack={(url) => api.trackPost(post.Post_Id, url)}
    />
  );
}

function rangeFor(view, anchor) {
  if (view === "week") {
    const start = mondayOf(anchor);
    return [start, addDays(start, 6)];
  }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = mondayOf(first);
  return [start, addDays(start, 41)];
}

function EventChip({ ev, onOpen, onDragStart }) {
  const tone = eventTone(ev);
  return (
    <button
      type="button"
      className={`wk-ev wk-pill--${tone}${ev.Is_Overdue ? " is-late" : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, ev)}
      onClick={() => onOpen(ev)}
      title={`${ev.Title}${ev.Platform ? ` · ${ev.Platform}` : ""}${ev.Tenant_Name ? ` · ${ev.Tenant_Name}` : ""}`}
      aria-label={`${ev.Title}, ${ev.Status_Label || ev.Status || ""}${ev.Is_Overdue ? ", late" : ""}. Open details`}
    >
      {ev.Time ? <span className="wk-ev__icon">{fmtTime(ev.Time).replace(":00", "")}</span> : null}
      <span className="wk-ev__text">
        {ev.Platform ? `${ev.Platform}: ` : ""}
        {ev.Title}
      </span>
    </button>
  );
}

/**
 * Posting calendar map: planned post dates of work items plus DM calendar posts, coloured by status,
 * with empty future days highlighted. Drag a post to another day (or use "Move to" in its details).
 */
function PostingCalendar({ api, fixedFilters, showFilters = true }) {
  const notify = useNotify();
  const fixedKey = JSON.stringify(fixedFilters || {});
  const fixed = useMemo(() => JSON.parse(fixedKey), [fixedKey]);
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const [filters, setFilters] = useState({ tenant_id: "", area_id: "", user_id: "", status: "" });
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [moveDate, setMoveDate] = useState("");
  const [dropDay, setDropDay] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [packItem, setPackItem] = useState(null);
  const [clients, setClients] = useState([]);

  const [start, end] = useMemo(() => rangeFor(view === "agenda" ? "month" : view, anchor), [view, anchor]);
  const today = toISODate();

  const load = useCallback(async () => {
    try {
      const params = {
        date_from: toISODate(view === "agenda" ? new Date(anchor.getFullYear(), anchor.getMonth(), 1) : start),
        date_to: toISODate(view === "agenda" ? new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0) : end),
        ...Object.fromEntries(Object.entries({ ...filters, ...fixed }).filter(([, v]) => v !== "" && v != null)),
      };
      const res = await api.calendar(params);
      setData(res);
      setClients((prev) => {
        const map = new Map(prev.map((c) => [c.id, c.name]));
        (res.events || []).forEach((e) => e.tenant_id && e.Tenant_Name && map.set(e.tenant_id, e.Tenant_Name));
        return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
      });
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the calendar"));
    }
  }, [api, start, end, view, anchor, filters, fixed]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setMoveDate("");
  }, [selected]);

  useEffect(() => {
    api.meta().then(setMeta).catch(() => null);
  }, [api]);

  const byDay = useMemo(() => {
    const map = {};
    (data?.events || []).forEach((e) => {
      (map[e.Date] = map[e.Date] || []).push(e);
    });
    return map;
  }, [data]);
  const gaps = useMemo(() => new Set(data?.gaps || []), [data]);

  const shift = (dir) => {
    if (view === "week") setAnchor((a) => addDays(a, 7 * dir));
    else setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
  };

  const move = async (ev, date) => {
    if (!date || date === ev.Date) return;
    try {
      await api.moveOnCalendar({ kind: ev.Kind, id: ev.Id, date });
      notify.success(`Moved to ${fmtShortDate(date)}`);
      setSelected(null);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Could not move"));
    }
  };

  const onDragStart = (e, ev) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ kind: ev.Kind, id: ev.Id, date: ev.Date }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = (e, day) => {
    e.preventDefault();
    setDropDay(null);
    try {
      const ev = JSON.parse(e.dataTransfer.getData("text/plain"));
      move({ Kind: ev.kind, Id: ev.id, Date: ev.date }, day);
    } catch {
      /* not one of our chips */
    }
  };

  const markPosted = async (ev) => {
    try {
      await api.markCalendarPostPosted(ev.Post_Id, {});
      notify.success("Marked as posted");
      setSelected(null);
      load();
    } catch (err) {
      notify.error(errorMessage(err));
    }
  };

  const title =
    view === "week" ? `${fmtShortDate(toISODate(start))} – ${fmtShortDate(toISODate(end))}` : monthFmt.format(anchor);

  const renderDay = (d) => {
    const iso = toISODate(d);
    const events = byDay[iso] || [];
    const outside = view === "month" && d.getMonth() !== anchor.getMonth();
    const isGap = gaps.has(iso) && !outside;
    return (
      <div
        key={iso}
        className={`wk-cal-day${outside ? " is-outside" : ""}${iso === today ? " is-today" : ""}${isGap ? " is-gap" : ""}${dropDay === iso ? " is-drop" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDropDay(iso);
        }}
        onDragLeave={() => setDropDay((cur) => (cur === iso ? null : cur))}
        onDrop={(e) => onDrop(e, iso)}
        aria-label={`${dayFmt.format(d)}: ${events.length ? `${events.length} planned` : "nothing planned"}`}
      >
        <div className="wk-cal-day__num">
          <span>{d.getDate()}</span>
          {isGap ? <span className="wk-cal-day__gap">No post</span> : null}
        </div>
        {(view === "week" ? events : events.slice(0, 4)).map((ev) => (
          <EventChip key={`${ev.Kind}-${ev.Id}`} ev={ev} onOpen={setSelected} onDragStart={onDragStart} />
        ))}
        {view !== "week" && events.length > 4 ? (
          <button type="button" className="dmp-link" style={{ fontSize: 11 }} onClick={() => setView("agenda")}>
            +{events.length - 4} more
          </button>
        ) : null}
      </div>
    );
  };

  const days = [];
  for (let d = start; d <= end; d = addDays(d, 1)) days.push(d);
  const agendaDays = Object.keys(byDay).sort();

  return (
    <div className="wk">
      <div className="wk-cal-toolbar">
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => shift(-1)} aria-label="Previous">
          <FaChevronLeft aria-hidden="true" />
        </button>
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => setAnchor(new Date())}>
          Today
        </button>
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={() => shift(1)} aria-label="Next">
          <FaChevronRight aria-hidden="true" />
        </button>
        <h2 className="wk-cal-toolbar__title" aria-live="polite">
          {title}
        </h2>
        <div className="wk-seg" role="group" aria-label="Calendar view" style={{ marginLeft: "auto" }}>
          {["month", "week", "agenda"].map((v) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {showFilters ? (
        <div className="wk-cal-filters">
          {api.isStaff && !fixed.tenant_id ? (
            <select value={filters.tenant_id} onChange={(e) => setFilters((f) => ({ ...f, tenant_id: e.target.value }))} aria-label="Client">
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : null}
          <select value={filters.area_id} onChange={(e) => setFilters((f) => ({ ...f, area_id: e.target.value }))} aria-label="Area">
            <option value="">All areas</option>
            {(meta?.areas || []).map((a) => (
              <option key={a.Area_Id} value={a.Area_Id}>
                {a.Area_Name}
              </option>
            ))}
          </select>
          <select value={filters.user_id} onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))} aria-label="Person">
            <option value="">Everyone</option>
            {(meta?.people || []).map((p) => (
              <option key={p.User_Id} value={p.User_Id}>
                {p.Name}
              </option>
            ))}
          </select>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} aria-label="Status">
            <option value="">Any status</option>
            {(meta?.statuses || []).map((s) => (
              <option key={s} value={s}>
                {meta?.status_labels?.[s] || s}
              </option>
            ))}
          </select>
          {data ? (
            <span className="wk-muted" style={{ alignSelf: "center", fontSize: 13 }}>
              {data.summary.planned} planned · {data.summary.posted} posted · {data.summary.overdue} late · {data.gaps.length} empty days ahead
            </span>
          ) : null}
        </div>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}

      {view === "agenda" ? (
        <div className="dmp-card">
          {agendaDays.length ? (
            <ul className="wk-agenda">
              {agendaDays.map((iso) => (
                <li key={iso}>
                  <div className="wk-agenda__day">
                    <span>{dayFmt.format(parseISODate(iso))}</span>
                    {iso === today ? <span className="wk-count">Today</span> : null}
                  </div>
                  {byDay[iso].map((ev) => (
                    <button key={`${ev.Kind}-${ev.Id}`} type="button" className="wk-agenda__row" onClick={() => setSelected(ev)}>
                      <span className="wk-muted" style={{ minWidth: 64 }}>
                        {ev.Time ? fmtTime(ev.Time) : "Any time"}
                      </span>
                      <strong style={{ flex: 1 }}>
                        {ev.Platform ? `${ev.Platform}: ` : ""}
                        {ev.Title}
                      </strong>
                      <span className="wk-muted">{[ev.Project_Name, ev.Tenant_Name].filter(Boolean).join(" · ")}</span>
                      <span className="wk-muted">{ev.Assigned_Name || "Unassigned"}</span>
                      {ev.Kind === "item" ? <StatusPill status={ev.Status} small /> : <span className="wk-count">{ev.Status || "Draft"}</span>}
                    </button>
                  ))}
                </li>
              ))}
            </ul>
          ) : (
            <div className="wk-muted">Nothing planned this month.</div>
          )}
        </div>
      ) : (
        <div className={`wk-cal-grid${view === "week" ? " wk-cal-grid--week" : ""}`} role="grid" aria-label="Posting calendar">
          {DOW.map((d) => (
            <div key={d} className="wk-cal-dow" role="columnheader">
              {d}
            </div>
          ))}
          {days.map(renderDay)}
        </div>
      )}

      <div className="wk-legend" aria-label="Legend">
        {["Not Started", "In Progress", "In Review", "Approved", "Scheduled", "Posted", "Blocked"].map((s) => (
          <StatusPill key={s} status={s} small />
        ))}
        <span className="wk-cal-day__gap" style={{ alignSelf: "center" }}>
          Striped day = nothing planned
        </span>
      </div>

      {selected ? (
        <Modal
          title={selected.Title}
          onClose={() => setSelected(null)}
          footer={
            <>
              {selected.Kind === "item" ? (
                <>
                  <button
                    type="button"
                    className="dmp-btn dmp-btn--ghost wk-btn-lg"
                    onClick={() => {
                      setOpenItem(selected.Item_Id);
                      setSelected(null);
                    }}
                  >
                    Open details
                  </button>
                  {!selected.Is_Closed ? (
                    <button
                      type="button"
                      className="dmp-btn wk-btn-lg"
                      onClick={() => {
                        setPackItem(selected.Item_Id);
                        setSelected(null);
                      }}
                    >
                      Ready to post
                    </button>
                  ) : null}
                </>
              ) : !selected.Is_Closed ? (
                <button type="button" className="dmp-btn wk-btn-lg" onClick={() => markPosted(selected)}>
                  Mark posted
                </button>
              ) : null}
            </>
          }
        >
          <dl className="wk-kv">
            <dt>Status</dt>
            <dd>{selected.Kind === "item" ? <StatusPill status={selected.Status} /> : selected.Status || "Draft"}</dd>
            <dt>Planned</dt>
            <dd>
              {fmtShortDate(selected.Date)}
              {selected.Time ? ` at ${fmtTime(selected.Time)}` : ""}
              {selected.Is_Overdue ? <span className="wk-count wk-count--red" style={{ marginLeft: 6 }}>Late</span> : null}
            </dd>
            <dt>Where</dt>
            <dd>{selected.Platform || "—"}</dd>
            <dt>Project</dt>
            <dd>{[selected.Project_Name, selected.Tenant_Name].filter(Boolean).join(" · ") || "—"}</dd>
            {selected.Area_Name ? (
              <>
                <dt>Area</dt>
                <dd>{selected.Area_Name}</dd>
              </>
            ) : null}
            <dt>Doing it</dt>
            <dd>{selected.Assigned_Name || "Unassigned"}</dd>
            {selected.Posted_URL ? (
              <>
                <dt>Live</dt>
                <dd>
                  <a href={selected.Posted_URL} target="_blank" rel="noreferrer">
                    Open post <FaExternalLinkAlt aria-hidden="true" size={11} />
                  </a>
                </dd>
              </>
            ) : null}
          </dl>
          {!selected.Is_Closed ? (
            <form
              className="wk-quick-add"
              onSubmit={(e) => {
                e.preventDefault();
                move(selected, moveDate);
              }}
            >
              <label className="wk-sr-only" htmlFor="wk-move-date">
                Move to date
              </label>
              <input id="wk-move-date" type="date" value={moveDate || selected.Date} onChange={(e) => setMoveDate(e.target.value)} />
              <button type="submit" className="dmp-btn" disabled={!moveDate || moveDate === selected.Date}>
                Move to this date
              </button>
            </form>
          ) : null}
          {selected.Kind === "post" && String(selected.Status || "").toLowerCase() === "posted" ? (
            <PostTracking api={api} post={selected} />
          ) : null}
        </Modal>
      ) : null}

      {openItem ? <ItemDrawer api={api} itemId={openItem} meta={meta} onClose={() => setOpenItem(null)} onChanged={load} /> : null}
      {packItem ? <ReadyPack api={api} itemId={packItem} onClose={() => setPackItem(null)} onPosted={load} /> : null}
    </div>
  );
}

export default PostingCalendar;
